import { inject, Injectable } from '@angular/core';
import { EcaSecurityService } from '@drc-eca/eca-components-lib';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private ecaSecurityService = inject(EcaSecurityService);

  // draft version UI access
  static readonly ACE_TABE_LOCAL_SCANNING_UI_TESTER = 'TABE:eca-local-scanning-web-ui:tester';
  // diagnostics route guard
  static readonly ACE_TABE_VIEW_DIAGNOSTICS = 'TABE:eca-local-scanning-web-ui:diagnostics:view';
  // site selection
  static readonly ACE_LOCAL_SCANNING_API_VIEW = 'eca-local-scanning-web-api:view';

  permissions = new Map<string, boolean>();
  initialized = false;

  constructor() {
    const allPermissions = [
      PermissionService.ACE_TABE_LOCAL_SCANNING_UI_TESTER,
      PermissionService.ACE_TABE_VIEW_DIAGNOSTICS,
      PermissionService.ACE_LOCAL_SCANNING_API_VIEW,
    ];

    allPermissions.forEach((permission) => this.permissions.set(permission, false));
  }

  hasAnyPermission(...aces: string[]): Observable<boolean> {
    return this.checkArrayOfPermissions(aces, false);
  }

  hasEveryPermission(...aces: string[]): Observable<boolean> {
    return this.checkArrayOfPermissions(aces, true);
  }

  private getPermissions(): Observable<Map<string, boolean>> {
    if (this.initialized) {
      return of(this.permissions);
    }

    const tasks: Observable<boolean>[] = [];

    this.permissions.forEach((permission, ace) => {
      tasks.push(this.ecaSecurityService.userHasPermission(ace));
    });

    return forkJoin(tasks).pipe(
      map((perms: boolean[]) => {
        let index = 0;
        this.permissions.forEach((permission, ace) => {
          this.permissions.set(ace, perms[index]);
          index++;
        });

        this.initialized = true;
        return this.permissions;
      }),
      catchError((error) => /* istanbul ignore next */ {
        console.log(`An error occurred while attempting to retrieve permissions:`, error);
        return of(this.permissions);
      }),
    );
  }

  private checkArrayOfPermissions(aces: string[], satisfyAll: boolean): Observable<boolean> {
    if (!aces || aces.length === 0) {
      return of(false);
    }

    return this.getPermissions().pipe(
      take(1),
      map((permissionsMap: Map<string, boolean>) => {
        const perms: boolean[] = [];

         
        for (let index = 0; index < aces.length; index++) {
          const ace = aces[index];
          perms.push(permissionsMap.get(ace));
        }

        if (satisfyAll) {
          const every = perms.every(val => this.isTruthy(val));
          // console.log(`PermissionService: every(${aces.join(', ')}) === ${result}`);
          return every;
        }

        const some = perms.some(val => this.isTruthy(val));
        // console.log(`PermissionService: some(${aces.join(', ')}) === ${result}`);
        return some;
      }),
    );
  }

  private isTruthy(value) {
    return !!value;
  }
}
